import {
  FINANCIAL_INFO_TABLE_KEY,
  FINANCIAL_INFO_VIEWS_PATHS,
} from 'app/pages/loanApplication/tabs/financialInfo/FinancialInfo.constants';
import { createSelector } from 'reselect';

import {
  assetsTypesOptions,
  incomeTypesOptions,
  liabilityExpenseTypeOptions,
} from 'constants/options';
import { getOptionTypeName } from 'utils/data';

import { selectFinancialInfoAllTablesData } from './financialInfo.selectors';
import { selectBorrowersTableData } from './loanApplicationBorrowers.selectors';

const getDocument = state => state.document;

/**
 * Build the shared options map (borrowers / incomes / assets / properties / liabilities)
 * used by the multi-value selector.
 */
const buildAssociationOptions = (finInfo, borrowers) => ({
  borrowers: borrowers?.map(borrower => ({ value: borrower.id, name: borrower.fullName })) ?? [],
  incomes:
    finInfo[FINANCIAL_INFO_TABLE_KEY[FINANCIAL_INFO_VIEWS_PATHS.INCOME_AND_EMPLOYMENT]]?.map(
      income => ({
        value: income.id,
        // Income edits are nested under a borrower (PUT .../borrower/{borrowerId}/income/{id}),
        // so the apply-to-entity flow needs the owning borrowerId alongside the income id.
        borrowerId: income.borrowerId,
        name: `${
          income.employerName ?? getOptionTypeName(incomeTypesOptions, income.incomeType)
        } | $${income.monthlyIncome}`,
      }),
    ) ?? [],
  assets:
    finInfo[FINANCIAL_INFO_TABLE_KEY[FINANCIAL_INFO_VIEWS_PATHS.ASSETS]]?.map(asset => ({
      value: asset.id,
      name: `${
        asset.institution ?? getOptionTypeName(assetsTypesOptions, asset.assetTypeName)
      } | $${asset.balance}`,
    })) ?? [],
  properties:
    finInfo[FINANCIAL_INFO_TABLE_KEY[FINANCIAL_INFO_VIEWS_PATHS.REO]]?.map(property => ({
      value: property.id,
      name: property.address,
    })) ?? [],
  liabilities:
    finInfo[FINANCIAL_INFO_TABLE_KEY[FINANCIAL_INFO_VIEWS_PATHS.LIABILITIES]]?.map(liability => ({
      value: liability.id,
      name: `${
        liability.creditorName ??
        getOptionTypeName(liabilityExpenseTypeOptions, liability.liabilityExpenseType)
      } | $${liability.monthlyPayment}`,
    })) ?? [],
});

/**
 * Given an array of scalar ids (from new collection-contract GET response) and option objects,
 * return the matching option objects — with a fallback to the legacy scalar id for documents
 * that haven't been re-saved under the new contract yet.
 *
 * @param {string[]} ids         - collection ids from the new response shape
 * @param {string|null} scalarId - legacy scalar id (primary / single)
 * @param {{ value: string }[]} options
 * @returns {{ value: string, name: string }[]}
 */
const resolveMultiValues = (ids, scalarId, options) => {
  if (ids?.length) {
    return options.filter(o => ids.includes(o.value));
  }

  if (scalarId) {
    const match = options.find(o => o.value === scalarId);

    return match ? [match] : [];
  }

  return [];
};

/**
 * Multi-value selector — consumed by DocumentLinksTab.
 *
 * Returns documentAssociationsInitialFormValues with the plural-array shape:
 *   { borrowers[], incomes[], assets[], properties[], liabilities[] }
 *
 * Handles both the new collection-contract response (borrowerIds[]) and the legacy
 * scalar response (borrowerId) via resolveMultiValues.
 */
export const selectDocumentAssociationsMulti = createSelector(
  [getDocument, selectFinancialInfoAllTablesData, selectBorrowersTableData],
  (state, finInfo, borrowers) => {
    const options = buildAssociationOptions(finInfo, borrowers);

    const initialFormValues = {
      borrowers: resolveMultiValues(state.borrowerIds, state.borrowerId, options.borrowers),
      incomes: resolveMultiValues(state.incomeIds, state.incomeId, options.incomes),
      assets: resolveMultiValues(state.assetIds, state.assetId, options.assets),
      properties: resolveMultiValues(state.propertyIds, state.propertyId, options.properties),
      liabilities: resolveMultiValues(state.liabilityIds, state.liabilityId, options.liabilities),
    };

    return {
      documentAssociationsOptions: options,
      documentAssociationsInitialFormValues: initialFormValues,
    };
  },
);
